import { filterBlank } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { safeParseInt } from '@dte/otw-utils-core/src/numbers/safeParseInt';
import { normalizeValue } from '@dte/otw-utils-core/src/strings/normalizeValue';
import { differenceInYears } from 'date-fns/differenceInYears';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
// TODO: change to ESM import when https://github.com/validatorjs/validator.js/issues/2528 is fixed
import isNumeric from 'validator/es/lib/isNumeric';
import type { DateRepresentation } from '../DateRepresentation';

function parseNumericDate(date: number | undefined): Date | undefined {
	if (!date) {
		return undefined;
	}

	try {
		// If the date is in milliseconds, adding 1000 to it will result in a date will generally result in a date that's far in the future
		const futureDate = new Date(date * 1000);
		const yearsAhead = differenceInYears(futureDate, new Date());
		if (yearsAhead > 500) {
			return new Date(date);
		}

		// TODO: this approach has gaps for dates in the early 1970's

		// Assume the date was in seconds
		return futureDate;
	} catch {
		// Unable to parse date
	}

	// If the number doesn't match expected lengths, return undefined
	return undefined;
}

function parseStringDate(date: string): Date | undefined {
	const normalized = normalizeValue(date);
	if (!normalized) {
		return undefined;
	}

	try {
		const parsed = parseISO(normalized);
		if (isValid(parsed)) {
			return parsed;
		}
	} catch {
		// Unable to parse date
	}

	return undefined;
}

export function safeParseDate(date: DateRepresentation): Date | undefined {
	// Date is undefined
	if (!date) {
		return undefined;
	}

	// Date is already a date
	if (date instanceof Date) {
		return date;
	}

	// Date is a string
	if (typeof date === 'string') {
		const dateValue = parseStringDate(date);
		if (dateValue) {
			return dateValue;
		}

		// For strings, stop unless this looks like a number
		if (!isNumeric(date)) {
			return undefined;
		}
	}

	// If we weren't able to parse it as a string, try seeing if the value is numeric
	const numericValue = safeParseInt(date);
	return parseNumericDate(numericValue);
}

export function safeParseDates(dates: DateRepresentation[]): Date[] {
	const dateValues = dates?.map(safeParseDate).filter((date) => date);
	return filterBlank(dateValues);
}
